
import Vue from 'vue'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'

if (process.client) {
  const VueCryptojs = require('vue-cryptojs').default
  Vue.use(VueCryptojs)
}

// Google Identity Services client ID
const GOOGLE_CLIENT_ID = '75229862004-unrkhhktd5ip381qro36vig0hueisv2j.apps.googleusercontent.com'

declare global {
  interface Window {
    AppleID: any
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void
          prompt: (callback?: (notification: any) => void) => void
          renderButton: (element: HTMLElement, config: any) => void
          cancel: () => void
        }
      }
    }
  }
}

export default Vue.extend({
  name: 'LoginV2',
  components: {
    InputText: () => import('primevue/inputtext'),
    Button: () => import('primevue/button'),
    Checkbox: () => import('primevue/checkbox'),
    OtpInput: () => import('~/components/OtpInput.vue'),
    VuePhoneNumberInput: () => import('vue-phone-number-input')
  },
  layout: 'no-footer',
  data() {
    return {
      submitTried: false,
      email: '',
      password: '',
      otp: '',
      error: '',
      emailError: '',
      otpError: '',
      showOtpInput: false,
      otpRemainingTime: 0,
      otpTimerInterval: null,
      resendingOtp: false,
      otpMethod: 'email',
      forceEmailOtp: false,
      $gtm: null,
      showPassword: false,
      suggestion: null,
      secret: 'CVing secret',
      conditionalLoginSupported: false,
      isInAppBrowser: false,
      debugUserAgent: '',
      // Google signup flow (for new users)
      showGoogleComplete: false,
      googleIdToken: '',
      googleLoading: false,
      googleFirstName: '',
      googleLastName: '',
      googleEmail: '',
      socialPhoneNumber: '',
      socialFormattedNumber: '',
      socialPhoneIsValid: false,
      terms1: false,
      terms2: false,
      // LinkedIn signup flow
      linkedinLoading: false,
      linkedinSignupToken: '',
      // Facebook signup flow
      facebookLoading: false,
      facebookSignupToken: '',
      // Apple signup flow
      appleSignupToken: '',
      socialProvider: '' as '' | 'google' | 'linkedin' | 'facebook' | 'apple',
      // Flag to determine if name fields should be editable
      nameFieldsEditable: false
    }
  },
  head() {
    return {
      title: 'Login - CVing',
      meta: [{ hid: 'robots', name: 'robots', content: 'noindex,follow' }]
    }
  },
  computed: {
    orpSuggestion() {
      if (process.client) {
        const pendingOrp = sessionStorage.getItem('pending_orp_suggestion')
        return pendingOrp ? JSON.parse(pendingOrp) : null
      }
      return null
    },
    formatOtpTime() {
      const minutes = Math.floor(this.otpRemainingTime / 60)
      const seconds = this.otpRemainingTime % 60
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
    socialProviderIcon() {
      const icons: Record<string, string> = {
        google: 'pi-google google-icon',
        linkedin: 'pi-linkedin linkedin-icon',
        facebook: 'pi-facebook facebook-icon'
      }
      return icons[this.socialProvider] || 'pi-google google-icon'
    },
    socialProviderDataInfoKey() {
      const keys: Record<string, string> = {
        google: 'signup.googleDataInfo',
        linkedin: 'signup.linkedinDataInfo',
        facebook: 'signup.facebookDataInfo'
      }
      return keys[this.socialProvider] || 'signup.googleDataInfo'
    },
    socialSignupLoading() {
      if (this.socialProvider === 'linkedin') { return this.linkedinLoading }
      if (this.socialProvider === 'facebook') { return this.facebookLoading }
      return this.googleLoading
    },
    hideSocialLogins() {
      return !!this.$route.query.email
    },
    otpMessage() {
      if (this.otpMethod === 'sms') {
        return this.$t('login.enterOtpMessageWithSms')
      } else if (this.otpMethod === 'whatsapp') {
        return this.$t('login.enterOtpMessageWithWhatsapp')
      }
      return this.$t('login.enterOtpMessageWithEmail', { email: this.email })
    }
  },
  created() {
    if (this.$auth?.loggedIn) {
      this.$router.push(this.localePath('index'))
    }
    if (this.orpSuggestion?.cid) {
      this.track('login', 'start', '')
    }
  },
  mounted() {
    this.$nextTick(async () => {
      window?.AppleID?.auth.init({
        clientId: 'com.cving.appleidlogin', // This is the service ID we created.
        scope: 'name email', // To tell apple we want the user name and emails fields in the response it sends us.
        redirectURI: this.getAppleRedirectUri(), // Dynamic redirect URI based on current environment
        state: 'origin:web', // Any string of your choice that you may use for some logic. It's optional and you may omit it.
        usePopup: true // Important if we want to capture the data apple sends on the client side.
      })

      // DEBUG: Capture user agent for testing
      this.debugUserAgent = navigator.userAgent || ''

      // Check if we're in an in-app browser (WebView) where WebAuthn doesn't work properly
      this.isInAppBrowser = this.$webauthnUtils?.isInAppBrowser?.() || false
      console.log('[Passkey Debug] Is in-app browser:', this.isInAppBrowser)
      console.log('[Passkey Debug] User Agent:', this.debugUserAgent)

      // Check if WebAuthn is supported for conditional login (skip if in-app browser)
      console.log('[Passkey Debug] WebAuthn available:', this.$webauthnUtils?.isAvailable())
      if (this.$webauthnUtils?.isAvailable() && !this.isInAppBrowser) {
        // Check if Conditional Mediation (Passkey Autofill) is supported
        try {
          this.conditionalLoginSupported = await this.$webauthnUtils.isConditionalMediationSupported()
          console.log('[Passkey Debug] Conditional Mediation supported:', this.conditionalLoginSupported)
          if (this.conditionalLoginSupported) {
            console.log('[Passkey Debug] Starting conditional login...')
            this.startConditionalLogin()
          }
        } catch (error) {
          console.warn('[Passkey Debug] Conditional mediation check failed:', error)
        }
      } else if (this.isInAppBrowser) {
        console.log('[Passkey Debug] Skipping passkey features - in-app browser detected')
      }
    })

    // Pre-fill email from query parameter
    if (this.$route.query.email) {
      this.email = this.$route.query.email.toString()
    }

    // Restore OTP state from localStorage if available (e.g., after page refresh)
    this.$nextTick(() => {
      if (this.email) {
        this.restoreOtpState()
      }
    })
  },
  beforeDestroy() {
    this.clearOtpTimer();
    // Abort any pending conditional login
    (this.$webauthn as any)?.abortConditionalLogin?.()
  },
  methods: {

    setOrpOutcome(payload: { suggestCode: string }) {
      if (process.client) {
        sessionStorage.setItem('orp_outcome', JSON.stringify(payload))
      }
    },
    async buildRefferedDirectInviteData() {
      if (!this.orpSuggestion?.cid) { return null }

      let clientId = null
      let sessionId = null

      if (process.client && (window as any)?.gtag) {
        try {
          clientId = await new Promise((resolve) => {
            (window as any).gtag('get', 'G-QB45E7C0YX', 'client_id', (id: string) => resolve(id))
          })
          sessionId = await new Promise((resolve) => {
            (window as any).gtag('get', 'G-QB45E7C0YX', 'session_id', (id: number) => resolve(Number(id)))
          })
        } catch (e) {
          console.warn('Could not get analytics IDs', e)
        }
      }

      return {
        nameReferredCandidate: this.orpSuggestion.name || '',
        lastNameReferredCandidate: this.orpSuggestion.surname || '',
        mailReferredCandidate: this.orpSuggestion.email || '',
        messageCustom: this.orpSuggestion.message || '',
        phoneNumber: this.orpSuggestion.phone || '',
        Cid: Number(this.orpSuggestion.cid),
        analytics: {
          client_id: clientId,
          session_id: sessionId
        }
      }
    },
    handleOtpInput() {
      this.submitTried = false
      this.otpError = ''
    },
    updateSocialPhoneNumber(data: any) {
      const newFormattedNumber = data.formattedNumber !== undefined ? data.formattedNumber : data.phoneNumber
      this.socialFormattedNumber = newFormattedNumber
      this.socialPhoneIsValid = data.isValid
    },
    handleOtpComplete(otp) {
      // Auto-submit when all 6 digits are entered
      if (otp.length === 6) {
        this.loginUser()
      }
    },
    async startConditionalLogin() {
      if (!this.$webauthn || !this.conditionalLoginSupported) {
        return
      }

      try {
        const result = await this.$webauthn.startConditionalLogin()

        if (result.success) {
          // Login successful via passkey autofill
          await this.handleConditionalLoginSuccess(result.data)
        } else if (!result.aborted) {
          // Only show error if it wasn't an abort
          console.warn('Conditional login failed:', result.error)
        }
      } catch (error) {
        console.warn('Conditional login error:', error)
      }
    },
    async handleConditionalLoginSuccess(userData) {
      try {
        this.$nuxt.$loading?.start()

        // Set auth token
        this.$auth.setUserToken(userData.token)

        // Get fresh user data
        await this.getUserData()

        // Set locale based on user preference
        this.$i18n.setLocale(this.$auth.user?.locale || 'it')
        this.$dayjs.locale(this.$auth.user?.locale || 'it')

        // Track login event
        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(
          this.$auth.user?.email || '',
          this.secret
        ).toString()

        this.$gtm.push({
          event: 'login',
          event_data: {
            login_platform: 'main_site',
            login_method: 'webauthn_conditional',
            user_id: encryptedEmail,
            page_url: this.$router.currentRoute.fullPath,
            page_title: 'Login - CVing'
          }
        })

        // Track ORP if present
        if (this.orpSuggestion?.cid) {
          this.track('login', 'end', 'success')
        }

        this.$nuxt.$loading?.finish()
      } catch (error) {
        this.$nuxt.$loading?.finish()
        console.error('Error handling conditional login success:', error)
        this.error = this.$t('login.genericError').toString()
      }
    },
    validateEmail(email) {
      if (!email || typeof email !== 'string') {
        return false
      }
      return String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    },
    userSignup() {
      if (this.orpSuggestion?.cid) {
        this.track('login', 'end', 'signup')
      }
      this.$router.push({ name: `signup___${this.$i18n.locale}` })
    },
    track(stepName: string, stepStage: string, actionStep: string) {
      const ita = this.orpSuggestion?.company?.coid === 594
      const data = {
        company_name: this.orpSuggestion?.campaign?.company?.co_name,
        job_application_name: this.orpSuggestion?.campaign?.title,
        job_application_place: this.orpSuggestion?.campaign?.city,
        is_stage: this.orpSuggestion?.campaign?.contract_type === 8,
        verified: this.orpSuggestion?.campaign?.verified,
        orp: this.orpSuggestion?.campaign?.verified && !this.orpSuggestion?.campaign?.expired && !ita && this.orpSuggestion?.campaign?.p2p && this.$config.p2p,
        orp_value: Number(this.orpSuggestion?.campaign?.unlockamount),
        page_url: this.$router.currentRoute.fullPath,
        page_title: 'Login',
        step_name: stepName,
        step_stage: stepStage,
        action_step: actionStep,
        logged: this.orpSuggestion?.cid ? false : this.$auth?.loggedIn
      }
      this.$gtm.push({ event: 'orp_program_v2', event_data: data })
    },
    async requestOtp() {
      this.submitTried = false
      // Request OTP with optional method preference
      const requestData: any = { email: this.email.trim() }
      if (this.forceEmailOtp) {
        requestData.method = 'email'
      }

      const response = await this.$axios.post('/proxyapi/request-otp', requestData)

      // Check if user needs to register
      if (response.data?.requires_registration) {
        this.$nuxt.$loading?.finish()
        // Redirect to signup page with email prefilled
        this.$router.push(this.localePath({ name: 'signup', query: { email: this.email.trim() } }))
        return false
      }

      // Get the method used from response (defaults to 'email' for backward compatibility)
      this.otpMethod = response.data?.method_used || 'email'

      // Show OTP input and start timer
      this.showOtpInput = true
      // Use remaining_seconds from backend if available, otherwise default to 300 seconds
      const remainingTime = response.data?.remaining_seconds || 300
      this.startOtpTimer(remainingTime)
      // Save state to localStorage for recovery after page refresh
      this.saveOtpState()
      this.$nuxt.$loading?.finish()

      // Focus on OTP input
      this.$nextTick(() => {
        document.getElementById('otpinput')?.focus()
      })

      return true
    },

    async loginUser() {
      this.submitTried = true;
      // Abort any pending conditional login
      (this.$webauthn as any)?.abortConditionalLogin()

      // Validate email
      if (!this.email.trim()) {
        return
      }
      if (!this.validateEmail(this.email)) {
        this.emailError = this.$t('login.notValidEmail').toString()
        return
      }

      // Step 1: Try passkey first, then fallback to OTP
      if (!this.showOtpInput) {
        try {
          this.submitTried = false
          this.$nuxt.$loading?.start()
          this.error = ''

          // Try passkey login first (if available, not forcing email OTP, and not in-app browser)
          if (this.$webauthn && !this.forceEmailOtp && !this.isInAppBrowser) {
            try {
              // Use a shorter timeout (10 seconds) to quickly fallback to OTP if passkey doesn't work
              const passkeyResult = await this.$webauthn.login(this.email.trim(), 10000)

              if (passkeyResult.success) {
                // Passkey login succeeded
                this.$nuxt.$loading?.finish()
                await this.handleWebAuthnSuccess(passkeyResult.data)
                return
              }

              const errorStatus = passkeyResult.error?.response?.status
              const errorMessage = passkeyResult.error?.message || ''

              // 401 means user doesn't exist - redirect to signup
              if (errorStatus === 401) {
                this.$nuxt.$loading?.finish()
                this.$router.push(this.localePath({ name: 'signup', query: { email: this.email.trim() } }))
                return
              }

              // If timeout occurred (likely WebView issue), silently fallback to OTP
              if (errorMessage.includes('timeout')) {
                console.log('[Passkey Debug] Passkey timeout - falling back to OTP')
                // Continue with OTP flow
              } else if (errorStatus !== 404) {
                // If error is not 404 (no passkey), it might be user cancelled or other error
                // Only fallback to OTP if 404 (no passkey registered)
                // User cancelled or other error - don't automatically fall back
                this.$nuxt.$loading?.finish()
                // If user explicitly cancelled, don't show error
                if (passkeyResult.error?.name === 'NotAllowedError') {
                  return
                }
                // Show error for other cases
                this.error = this.$t('login.genericError').toString()
                return
              }
              // 404 means no passkey, continue with OTP flow
            } catch (passkeyError: any) {
              // If passkey check fails with 404 or timeout, continue with OTP
              if (passkeyError.response?.status !== 404) {
                console.error('Passkey login error:', passkeyError)
              }
              // Continue with OTP flow
            }
          }

          // Fallback to OTP flow
          await this.requestOtp()
        } catch (error: any) {
          this.$nuxt.$loading?.finish()
          this.error = error.response?.data?.error || this.$t('login.otpRequestError').toString()
        }
        return
      }

      // Step 2: Login with OTP
      if (!this.otp.trim()) {
        return
      }

      try {
        this.submitTried = false
        this.$nuxt.$loading?.start()
        this.error = ''
        this.otpError = ''

        const pendingMbti = process.client ? sessionStorage.getItem('pending_mbti') : null
        const refferedDirectInviteData = await this.buildRefferedDirectInviteData()
        const loginData: any = {
          email: this.email.trim(),
          otp: this.otp.trim(),
          mbti: pendingMbti || ''
        }
        if (refferedDirectInviteData) {
          loginData.refferedDirectInviteData = refferedDirectInviteData
        }
        const login = await this.$auth.loginWith('local', {
          data: loginData
        })

        this.$i18n.setLocale(this.$auth.user.locale || 'it')
        this.$dayjs.locale(this.$auth.user.locale || 'it')
        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(this.email.trim(), this.secret).toString()

        this.$gtm.push({ event: 'login', event_data: { login_platform: 'main_site', login_method: `otp_${this.otpMethod}`, user_id: encryptedEmail, page_url: this.$router.currentRoute.fullPath, page_title: 'Login - CVing' } })

        if (login && this.orpSuggestion?.cid) {
          this.track('login', 'end', 'login')
        }

        // Clear OTP timer and state from localStorage
        this.clearOtpTimer()
        this.clearOtpState()

        // Clear pending MBTI from sessionStorage
        if (process.client && pendingMbti) {
          sessionStorage.removeItem('pending_mbti')
        }

        this.$nuxt.$loading?.finish()

        // Try to register passkey after successful OTP login
        await this.tryRegisterPasskey()
      } catch (error: any) {
        this.$nuxt.$loading?.finish()
        if (error.response?.status === 401) {
          this.otpError = this.$t('login.invalidOtp').toString()
        } else {
          this.error = error.response?.data?.error || 'Errore durante il login'
        }
      }
    },

    async loginWithOtherMethod() {
      this.submitTried = true
      // Validate email
      if (!this.email.trim()) {
        return
      }
      try {
        this.$nuxt.$loading?.start()
        this.error = ''
        await this.requestOtp()
      } catch (error: any) {
        this.$nuxt.$loading?.finish()
        this.error = error.response?.data?.error || this.$t('login.otpRequestError').toString()
      }
    },
    decodeJwt(token: string): any {
      try {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
        return JSON.parse(jsonPayload)
      } catch (e) {
        console.error('Error decoding JWT:', e)
        return null
      }
    },

    async loginGoogle() {
      // Abort any pending conditional login
      (this.$webauthn as any)?.abortConditionalLogin()

      this.googleLoading = true
      this.error = ''

      try {
        this.$nuxt.$loading?.start()

        // Check if GIS is loaded
        if (!window.google?.accounts?.id) {
          throw new Error('Google Identity Services not loaded')
        }

        // Create a promise to handle the async callback
        const idToken = await new Promise<string>((resolve, reject) => {
          // Check if browser supports FedCM (Federated Credential Management)
          // Some browsers like Opera don't support it yet
          const supportsFedCM = 'IdentityCredential' in window

          // Fallback to OAuth popup when One Tap is not displayed
          const openOAuthPopup = () => {
            const width = 500
            const height = 600
            const left = window.screenX + (window.outerWidth - width) / 2
            const top = window.screenY + (window.outerHeight - height) / 2
            const redirectUri = window.location.origin + '/auth/google/callback'
            const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token id_token&scope=openid email profile&nonce=${Math.random().toString(36).substring(2)}`

            const popup = window.open(oauthUrl, 'googleOAuth', `width=${width},height=${height},left=${left},top=${top}`)

            if (!popup) {
              reject(new Error('popup_blocked'))
              return
            }

            // Listen for message from popup
            const handleMessage = (event: MessageEvent) => {
              if (event.origin !== window.location.origin) {
                return
              }
              if (event.data?.type === 'google-oauth-callback') {
                window.removeEventListener('message', handleMessage)
                if (event.data.id_token) {
                  resolve(event.data.id_token)
                } else if (event.data.error) {
                  reject(new Error(event.data.error))
                } else {
                  reject(new Error('No credential received'))
                }
              }
            }
            window.addEventListener('message', handleMessage)

            // Check if popup was closed without completing
            const checkClosed = setInterval(() => {
              if (popup.closed) {
                clearInterval(checkClosed)
                window.removeEventListener('message', handleMessage)
                reject(new Error('popup_closed_by_user'))
              }
            }, 500)
          }

          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: (response: any) => {
              if (response.credential) {
                resolve(response.credential)
              } else {
                reject(new Error('No credential received'))
              }
            },
            cancel_on_tap_outside: false,
            use_fedcm_for_prompt: supportsFedCM
          })

          // Trigger the sign-in prompt with notification callback
          window.google.accounts.id.prompt((notification: any) => {
            // If prompt is not displayed (blocked by browser, cooldown, etc.), use OAuth popup fallback
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              const reason = notification.getNotDisplayedReason?.() || notification.getSkippedReason?.() || 'unknown'
              console.log('Google One Tap not displayed, reason:', reason, '- falling back to OAuth popup')
              openOAuthPopup()
            } else if (notification.isDismissedMoment()) {
              const reason = notification.getDismissedReason?.() || 'unknown'
              if (reason === 'credential_returned') {
                // Success - callback will be called
              } else {
                reject(new Error('popup_closed_by_user'))
              }
            }
          })
        })

        // Decode JWT to get user info
        const payload = this.decodeJwt(idToken)
        const userEmail = payload?.email || ''

        // Store Google data for potential signup
        this.googleIdToken = idToken
        this.googleFirstName = payload?.given_name || ''
        this.googleLastName = payload?.family_name || ''
        this.googleEmail = userEmail

        // Try to login
        const pendingMbti = process.client ? sessionStorage.getItem('pending_mbti') : null
        const refferedDirectInviteData = await this.buildRefferedDirectInviteData()
        const params: any = { email: '', password: '', account: 3, dlt: '', id_token: idToken, mbti: pendingMbti || '' }
        if (refferedDirectInviteData) {
          params.refferedDirectInviteData = refferedDirectInviteData
        }
        const login = await this.$axios.post('/proxyapi/login-google', params)

        // Login successful
        this.$i18n.setLocale(this.$auth.user?.locale || 'it')
        this.$dayjs.locale(this.$auth.user?.locale || 'it')
        this.$auth.setUserToken(login.data.token)
        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(userEmail, this.secret).toString()

        this.$gtm.push({ event: 'login', event_data: { login_platform: 'main_site', login_method: 'google', user_id: encryptedEmail, page_url: this.$router.currentRoute.fullPath, page_title: 'Login - CVing' } })

        await this.getUserData()
        if (process.client && pendingMbti) {
          sessionStorage.removeItem('pending_mbti')
        }
        if (login && this.orpSuggestion?.cid) {
          this.track('login', 'end', 'login')
        }

        // Try to register passkey after successful Google login
        await this.tryRegisterPasskey()
      } catch (error: any) {
        if (error?.message === 'popup_closed_by_user') {
          // User closed popup, do nothing
        } else if (error?.message === 'popup_blocked') {
          this.error = 'Popup bloccato. Abilita i popup per questo sito e riprova.'
        } else if (error?.message === 'Google Identity Services not loaded' || error?.message === 'prompt_not_displayed') {
          this.error = 'Google non disponibile'
        } else if (error.response?.status === 404 || error.response?.data?.error === 'user_not_found') {
          // User not found - show signup completion form
          this.socialProvider = 'google'
          this.showGoogleComplete = true
          this.submitTried = false
          this.terms1 = false
          this.terms2 = false
          this.socialPhoneNumber = ''
          this.socialFormattedNumber = ''
          this.socialPhoneIsValid = false
          this.nameFieldsEditable = false // Google always provides names
        } else if (error.response?.status === 401) {
          this.error = this.$t('login.wrongUsername').toString()
        } else {
          this.error = 'Errore durante il login'
        }
      } finally {
        this.$nuxt.$loading?.finish()
        this.googleLoading = false
      }
    },

    cancelGoogleSignup() {
      this.socialProvider = ''
      this.showGoogleComplete = false
      this.googleIdToken = ''
      this.googleFirstName = ''
      this.googleLastName = ''
      this.googleEmail = ''
      this.socialPhoneNumber = ''
      this.socialFormattedNumber = ''
      this.socialPhoneIsValid = false
      this.terms1 = false
      this.terms2 = false
      this.error = ''
      this.nameFieldsEditable = false
    },

    async completeGoogleSignup() {
      this.submitTried = true
      this.error = ''

      // Validate phone
      if (!this.socialPhoneIsValid || !this.socialPhoneNumber) {
        return
      }

      this.googleLoading = true

      // Check for pending MBTI from personality test
      const pendingMbti = process.client ? sessionStorage.getItem('pending_mbti') : null
      const refferedDirectInviteData = await this.buildRefferedDirectInviteData()

      try {
        // Call backend to complete Google signup
        const params: any = {
          id_token: this.googleIdToken,
          first_name: this.googleFirstName,
          last_name: this.googleLastName,
          phone: this.socialFormattedNumber,
          mail_subscription: this.terms2,
          terms: true,
          mbti: pendingMbti || ''
        }
        if (refferedDirectInviteData) {
          params.refferedDirectInviteData = refferedDirectInviteData
        }
        const response = await this.$axios.post('/proxyapi/google-signup', params)

        // Set auth token
        this.$auth.setUserToken(response.data.token)

        // Track signup event
        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(this.googleEmail.trim(), this.secret).toString()
        this.$gtm.push({
          event: 'sign_up',
          event_data: {
            sign_up_platform: 'main_site',
            sign_up_method: 'google',
            user_id: encryptedEmail,
            page_url: this.$router.currentRoute.fullPath,
            page_title: document ? document?.title : 'CVing'
          }
        })

        // Handle ORP if present
        if (this.orpSuggestion && this.orpSuggestion.cid) {
          this.track('login', 'end', 'registered')
        }

        // Clear pending MBTI from sessionStorage
        if (process.client && pendingMbti) {
          sessionStorage.removeItem('pending_mbti')
        }

        // Try to register passkey after successful Google signup
        await this.tryRegisterPasskey()
      } catch (error: any) {
        console.error('Complete Google signup error:', error)
        this.error = error?.response?.data?.error || this.$t('signup.googleSignupError').toString()
      } finally {
        this.googleLoading = false
      }
    },

    // LinkedIn Login Methods
    getLinkedInRedirectUri() {
      if (process.client) {
        return `${window.location.origin}/auth/linkedin-callback`
      }
      return ''
    },

    async loginLinkedIn() {
      (this.$webauthn as any)?.abortConditionalLogin()
      this.linkedinLoading = true
      this.error = ''

      try {
        const clientId = '77aj8f97pj0k0k'
        const redirectUri = encodeURIComponent(this.getLinkedInRedirectUri())
        const state = Math.random().toString(36).substring(2, 15)
        const scope = encodeURIComponent('openid profile email')

        // Store state for validation
        sessionStorage.setItem('linkedin_oauth_state', state)

        const authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&scope=${scope}`

        // Open popup
        const width = 600
        const height = 700
        const left = window.screenX + (window.outerWidth - width) / 2
        const top = window.screenY + (window.outerHeight - height) / 2
        const popup = window.open(authUrl, 'linkedin-login', `width=${width},height=${height},left=${left},top=${top}`)

        if (!popup) {
          throw new Error('Popup blocked')
        }

        // Listen for message from popup
        const code = await new Promise<string>((resolve, reject) => {
          const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
              return
            }

            if (event.data?.type === 'linkedin-auth-success') {
              window.removeEventListener('message', handleMessage)
              // Validate state
              const savedState = sessionStorage.getItem('linkedin_oauth_state')
              if (event.data.state !== savedState) {
                reject(new Error('State mismatch'))
                return
              }
              sessionStorage.removeItem('linkedin_oauth_state')
              resolve(event.data.code)
            } else if (event.data?.type === 'linkedin-auth-error') {
              window.removeEventListener('message', handleMessage)
              reject(new Error(event.data.errorDescription || 'LinkedIn auth error'))
            }
          }

          window.addEventListener('message', handleMessage)

          // Check if popup was closed
          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed)
              window.removeEventListener('message', handleMessage)
              reject(new Error('popup_closed_by_user'))
            }
          }, 500)
        })

        // Send code to backend
        const pendingMbti = process.client ? sessionStorage.getItem('pending_mbti') : null
        const refferedDirectInviteData = await this.buildRefferedDirectInviteData()
        const params: any = {
          code,
          redirect_uri: this.getLinkedInRedirectUri(),
          mbti: pendingMbti || ''
        }
        if (refferedDirectInviteData) {
          params.refferedDirectInviteData = refferedDirectInviteData
        }
        const login = await this.$axios.post('/proxyapi/login-linkedin', params)

        // Login successful
        this.$i18n.setLocale(this.$auth.user?.locale || 'it')
        this.$dayjs.locale(this.$auth.user?.locale || 'it')
        this.$auth.setUserToken(login.data.token)

        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(login.data.email || '', this.secret).toString()
        this.$gtm.push({ event: 'login', event_data: { login_platform: 'main_site', login_method: 'linkedin', user_id: encryptedEmail, page_url: this.$router.currentRoute.fullPath, page_title: 'Login - CVing' } })

        await this.getUserData()
        if (process.client && pendingMbti) {
          sessionStorage.removeItem('pending_mbti')
        }

        if (this.orpSuggestion?.cid) {
          this.track('login', 'end', 'login')
        }

        // Try to register passkey after successful LinkedIn login
        await this.tryRegisterPasskey()
      } catch (error: any) {
        if (error?.message === 'popup_closed_by_user') {
          // User closed popup, do nothing
        } else if (error?.message === 'Popup blocked') {
          this.error = 'Popup bloccato. Abilita i popup per continuare.'
        } else if (error.response?.status === 404 || error.response?.data?.error === 'user_not_found') {
          // User not found - show signup completion form
          // Store LinkedIn data and signup_token from response
          this.linkedinSignupToken = error.response?.data?.signup_token || ''
          this.googleFirstName = error.response?.data?.first_name || ''
          this.googleLastName = error.response?.data?.last_name || ''
          this.googleEmail = error.response?.data?.email || ''
          this.socialProvider = 'linkedin'
          this.showGoogleComplete = true
          this.submitTried = false
          this.terms1 = false
          this.terms2 = false
          this.socialPhoneNumber = ''
          this.socialFormattedNumber = ''
          this.socialPhoneIsValid = false
          // LinkedIn may not provide names, make fields editable if empty
          this.nameFieldsEditable = !this.googleFirstName || !this.googleLastName
        } else if (error.response?.status === 401) {
          this.error = this.$t('login.wrongUsername').toString()
        } else {
          this.error = 'Errore durante il login con LinkedIn'
        }
      } finally {
        this.linkedinLoading = false
      }
    },

    async completeLinkedInSignup() {
      this.submitTried = true
      this.error = ''

      // Validate phone
      if (!this.socialPhoneIsValid || !this.socialPhoneNumber) {
        return
      }

      this.linkedinLoading = true

      // Check for pending MBTI from personality test
      const pendingMbti = process.client ? sessionStorage.getItem('pending_mbti') : null
      const refferedDirectInviteData = await this.buildRefferedDirectInviteData()

      try {
        const params: any = {
          signup_token: this.linkedinSignupToken,
          first_name: this.googleFirstName,
          last_name: this.googleLastName,
          phone: this.socialFormattedNumber,
          mail_subscription: this.terms2,
          terms: true,
          mbti: pendingMbti || ''
        }
        if (refferedDirectInviteData) {
          params.refferedDirectInviteData = refferedDirectInviteData
        }
        const response = await this.$axios.post('/proxyapi/linkedin-signup', params)

        this.$auth.setUserToken(response.data.token)

        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(this.googleEmail.trim(), this.secret).toString()
        this.$gtm.push({
          event: 'sign_up',
          event_data: {
            sign_up_platform: 'main_site',
            sign_up_method: 'linkedin',
            user_id: encryptedEmail,
            page_url: this.$router.currentRoute.fullPath,
            page_title: document ? document?.title : 'CVing'
          }
        })

        if (this.orpSuggestion && this.orpSuggestion.cid) {
          this.track('login', 'end', 'registered')
        }

        // Clear pending MBTI from sessionStorage
        if (process.client && pendingMbti) {
          sessionStorage.removeItem('pending_mbti')
        }

        // Try to register passkey after successful LinkedIn signup
        await this.tryRegisterPasskey()
      } catch (error: any) {
        console.error('Complete LinkedIn signup error:', error)
        this.error = error?.response?.data?.error || 'Errore durante la registrazione con LinkedIn'
      } finally {
        this.linkedinLoading = false
      }
    },

    // Facebook Login Methods
    getFacebookRedirectUri() {
      if (process.client) {
        return `${window.location.origin}/auth/facebook-callback`
      }
      return ''
    },

    async loginFacebook() {
      (this.$webauthn as any)?.abortConditionalLogin()
      this.facebookLoading = true
      this.error = ''

      try {
        const clientId = '726770816533174'
        const redirectUri = encodeURIComponent(this.getFacebookRedirectUri())
        const state = Math.random().toString(36).substring(2, 15)
        const scope = 'email,public_profile'

        // Store state for validation
        sessionStorage.setItem('facebook_oauth_state', state)

        const authUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&response_type=code`

        // Open popup
        const width = 600
        const height = 700
        const left = window.screenX + (window.outerWidth - width) / 2
        const top = window.screenY + (window.outerHeight - height) / 2
        const popup = window.open(authUrl, 'facebook-login', `width=${width},height=${height},left=${left},top=${top}`)

        if (!popup) {
          throw new Error('Popup blocked')
        }

        // Listen for message from popup
        const code = await new Promise<string>((resolve, reject) => {
          const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
              return
            }

            if (event.data?.type === 'facebook-auth-success') {
              window.removeEventListener('message', handleMessage)
              // Validate state
              const savedState = sessionStorage.getItem('facebook_oauth_state')
              if (event.data.state !== savedState) {
                reject(new Error('State mismatch'))
                return
              }
              sessionStorage.removeItem('facebook_oauth_state')
              resolve(event.data.code)
            } else if (event.data?.type === 'facebook-auth-error') {
              window.removeEventListener('message', handleMessage)
              reject(new Error(event.data.errorDescription || 'Facebook auth error'))
            }
          }

          window.addEventListener('message', handleMessage)

          // Check if popup was closed
          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed)
              window.removeEventListener('message', handleMessage)
              reject(new Error('popup_closed_by_user'))
            }
          }, 500)
        })

        // Send code to backend
        const pendingMbti = process.client ? sessionStorage.getItem('pending_mbti') : null
        const refferedDirectInviteData = await this.buildRefferedDirectInviteData()
        const params: any = {
          code,
          redirect_uri: this.getFacebookRedirectUri(),
          mbti: pendingMbti || ''
        }
        if (refferedDirectInviteData) {
          params.refferedDirectInviteData = refferedDirectInviteData
        }
        const login = await this.$axios.post('/proxyapi/login-facebook', params)

        // Login successful
        this.$i18n.setLocale(this.$auth.user?.locale || 'it')
        this.$dayjs.locale(this.$auth.user?.locale || 'it')
        this.$auth.setUserToken(login.data.token)

        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(login.data.email || '', this.secret).toString()
        this.$gtm.push({ event: 'login', event_data: { login_platform: 'main_site', login_method: 'facebook', user_id: encryptedEmail, page_url: this.$router.currentRoute.fullPath, page_title: 'Login - CVing' } })

        await this.getUserData()
        if (process.client && pendingMbti) {
          sessionStorage.removeItem('pending_mbti')
        }

        if (this.orpSuggestion?.cid) {
          this.track('login', 'end', 'login')
        }

        // Try to register passkey after successful Facebook login
        await this.tryRegisterPasskey()
      } catch (error: any) {
        if (error?.message === 'popup_closed_by_user') {
          // User closed popup, do nothing
        } else if (error?.message === 'Popup blocked') {
          this.error = 'Popup bloccato. Abilita i popup per continuare.'
        } else if (error.response?.status === 404 || error.response?.data?.error === 'user_not_found') {
          // User not found - show signup completion form
          this.facebookSignupToken = error.response?.data?.signup_token || ''
          this.googleFirstName = error.response?.data?.first_name || ''
          this.googleLastName = error.response?.data?.last_name || ''
          this.googleEmail = error.response?.data?.email || ''
          this.socialProvider = 'facebook'
          this.showGoogleComplete = true
          this.submitTried = false
          this.terms1 = false
          this.terms2 = false
          this.socialPhoneNumber = ''
          this.socialFormattedNumber = ''
          this.socialPhoneIsValid = false
          // Facebook may not provide names, make fields editable if empty
          this.nameFieldsEditable = !this.googleFirstName || !this.googleLastName
        } else if (error.response?.status === 401) {
          this.error = this.$t('login.wrongUsername').toString()
        } else {
          this.error = 'Errore durante il login con Facebook'
        }
      } finally {
        this.facebookLoading = false
      }
    },

    async completeFacebookSignup() {
      this.submitTried = true
      this.error = ''

      // Validate phone
      if (!this.socialPhoneIsValid || !this.socialPhoneNumber) {
        return
      }

      this.facebookLoading = true

      // Check for pending MBTI from personality test
      const pendingMbti = process.client ? sessionStorage.getItem('pending_mbti') : null
      const refferedDirectInviteData = await this.buildRefferedDirectInviteData()

      try {
        const params: any = {
          signup_token: this.facebookSignupToken,
          phone: this.socialFormattedNumber,
          mail_subscription: this.terms2,
          terms: true,
          mbti: pendingMbti || ''
        }
        if (refferedDirectInviteData) {
          params.refferedDirectInviteData = refferedDirectInviteData
        }
        const response = await this.$axios.post('/proxyapi/facebook-signup', params)

        this.$auth.setUserToken(response.data.token)

        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(this.googleEmail.trim(), this.secret).toString()
        this.$gtm.push({
          event: 'sign_up',
          event_data: {
            sign_up_platform: 'main_site',
            sign_up_method: 'facebook',
            user_id: encryptedEmail,
            page_url: this.$router.currentRoute.fullPath,
            page_title: document ? document?.title : 'CVing'
          }
        })

        if (this.orpSuggestion && this.orpSuggestion.cid) {
          this.track('login', 'end', 'registered')
        }

        // Clear pending MBTI from sessionStorage
        if (process.client && pendingMbti) {
          sessionStorage.removeItem('pending_mbti')
        }

        // Try to register passkey after successful Facebook signup
        await this.tryRegisterPasskey()
      } catch (error: any) {
        console.error('Complete Facebook signup error:', error)
        this.error = error?.response?.data?.error || 'Errore durante la registrazione con Facebook'
      } finally {
        this.facebookLoading = false
      }
    },

    completeSocialSignup() {
      if (this.socialProvider === 'linkedin') {
        return this.completeLinkedInSignup()
      } else if (this.socialProvider === 'facebook') {
        return this.completeFacebookSignup()
      } else if (this.socialProvider === 'apple') {
        return this.completeAppleSignup()
      }
      return this.completeGoogleSignup()
    },

    // Apple Login Methods
    getAppleRedirectUri() {
      if (process.client) {
        return `${window.location.origin}/auth/apple-callback`
      }
      return ''
    },

    async loginApple () {
      let userEmail = ''
      try {
        this.$nuxt.$loading.start()
        this.error = ''
        const response = await window.AppleID.auth.signIn()
        console.log(response)

        // Decode the id_token JWT to extract user email (only for GTM tracking)
        const idToken = response.authorization.id_token
        const payload = this.decodeJwt(idToken)
        userEmail = payload?.email || ''

        console.log('Apple user email:', userEmail)

        // Extract first_name and last_name from Apple response (only available on first sign in)
        const firstName = response.user?.name?.firstName || ''
        const lastName = response.user?.name?.lastName || ''

        console.log('Apple user name:', firstName, lastName)

        const pendingMbti = process.client ? sessionStorage.getItem('pending_mbti') : null
        const refferedDirectInviteData = await this.buildRefferedDirectInviteData()
        const params: any = {
          email: '',
          password: '',
          account: 3,
          dlt: '',
          apple_token: response.authorization.code,
          id_token: idToken,
          redirect_uri: this.getAppleRedirectUri(),
          first_name: firstName,
          last_name: lastName,
          mbti: pendingMbti || ''
        }
        if (refferedDirectInviteData) {
          params.refferedDirectInviteData = refferedDirectInviteData
        }
        const login = await this.$axios.post('/proxyapi/login-apple', params)

        // Login successful
        this.$i18n.setLocale(this.$auth.user?.locale || 'it')
        this.$dayjs.locale(this.$auth.user?.locale || 'it')
        this.$auth.setUserToken(login.data.token)
        await this.$axios.get('/proxyapi/auth/get-csrf-token')
        await this.$axios.get('/proxystorage/auth/get-csrf-token')
        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(userEmail, this.secret).toString()

        this.$gtm.push({
          event: 'login',
          event_data: {
            login_platform: 'main_site',
            login_method: 'apple',
            user_id: encryptedEmail,
            page_url: this.$router.currentRoute.fullPath,
            page_title: 'Login - CVing'
          }
        })
        await this.getUserData()
        if (process.client && pendingMbti) {
          sessionStorage.removeItem('pending_mbti')
        }

        // Try to register passkey after successful Apple login
        await this.tryRegisterPasskey()
      } catch (error : any) {
        console.error('Apple login error:', error)
        if (error.response !== undefined && error.response.status === 401) {
          this.error = this.$t('login.wrongUsername').toString()
        } else if (error.response?.status === 404 || error.response?.data?.error === 'user_not_found') {
          // User not found - show signup completion form
          this.appleSignupToken = error.response?.data?.signup_token || ''
          this.googleFirstName = error.response?.data?.first_name || ''
          this.googleLastName = error.response?.data?.last_name || ''
          this.googleEmail = error.response?.data?.email || userEmail
          this.socialProvider = 'apple'
          this.showGoogleComplete = true
          this.submitTried = false
          this.terms1 = false
          this.terms2 = false
          this.socialPhoneNumber = ''
          this.socialFormattedNumber = ''
          this.socialPhoneIsValid = false
          // Apple only provides names on first sign in, make fields editable if empty
          this.nameFieldsEditable = !this.googleFirstName || !this.googleLastName
        } else if (error === false) {
          this.error = 'Apple not defined'
        } else {
          this.error = 'Errore durante il login con Apple'
        }
      } finally {
        this.$nuxt.$loading?.finish()
      }
    },

    async completeAppleSignup() {
      this.submitTried = true
      this.error = ''

      // Validate first name and last name
      if (!this.googleFirstName || !this.googleLastName) {
        return
      }

      // Validate phone
      if (!this.socialPhoneIsValid || !this.socialPhoneNumber) {
        return
      }

      this.googleLoading = true

      // Check for pending MBTI from personality test
      const pendingMbti = process.client ? sessionStorage.getItem('pending_mbti') : null
      const refferedDirectInviteData = await this.buildRefferedDirectInviteData()

      try {
        const params: any = {
          signup_token: this.appleSignupToken,
          first_name: this.googleFirstName,
          last_name: this.googleLastName,
          phone: this.socialFormattedNumber,
          mail_subscription: this.terms2,
          terms: true,
          mbti: pendingMbti || ''
        }
        if (refferedDirectInviteData) {
          params.refferedDirectInviteData = refferedDirectInviteData
        }
        const response = await this.$axios.post('/proxyapi/apple-signup', params)

        this.$auth.setUserToken(response.data.token)

        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(this.googleEmail.trim(), this.secret).toString()
        this.$gtm.push({
          event: 'sign_up',
          event_data: {
            sign_up_platform: 'main_site',
            sign_up_method: 'apple',
            user_id: encryptedEmail,
            page_url: this.$router.currentRoute.fullPath,
            page_title: document ? document?.title : 'CVing'
          }
        })

        if (this.orpSuggestion && this.orpSuggestion.cid) {
          this.track('login', 'end', 'registered')
        }

        // Clear pending MBTI from sessionStorage
        if (process.client && pendingMbti) {
          sessionStorage.removeItem('pending_mbti')
        }

        // Try to register passkey after successful Apple signup
        await this.tryRegisterPasskey()
      } catch (error: any) {
        console.error('Complete Apple signup error:', error)
        this.error = error?.response?.data?.error || 'Errore durante la registrazione con Apple'
      } finally {
        this.googleLoading = false
      }
    },

    async getUserData() {
      try {
        const user = await this.$axios.get('/proxyapi/auth/get-user-data')
        this.$auth.setUser(user.data)
      } catch (error) {
        console.error(error)
      }
      this.$nuxt.$loading?.finish()
    },
    startOtpTimer(seconds: number) {
      this.otpRemainingTime = seconds
      this.clearOtpTimer()
      this.otpTimerInterval = setInterval(() => {
        this.otpRemainingTime--
        if (this.otpRemainingTime <= 0) {
          this.clearOtpTimer()
        }
      }, 1000)
    },
    clearOtpTimer() {
      if (this.otpTimerInterval) {
        clearInterval(this.otpTimerInterval)
        this.otpTimerInterval = null
      }
    },
    // OTP localStorage helpers
    getOtpStorageKey() {
      // Sanitize email for use in localStorage key
      const sanitizedEmail = this.email.toLowerCase().replace(/[^a-z0-9]/gi, '_')
      return `otp_state_${sanitizedEmail}`
    },
    saveOtpState() {
      if (!process.client || !this.email) {
        return
      }
      const expiresAt = Date.now() + (this.otpRemainingTime * 1000)
      const state = {
        email: this.email,
        otpMethod: this.otpMethod,
        expiresAt,
        showOtpInput: this.showOtpInput
      }
      try {
        localStorage.setItem(this.getOtpStorageKey(), JSON.stringify(state))
      } catch (error) {
        console.warn('Failed to save OTP state to localStorage:', error)
      }
    },
    restoreOtpState() {
      if (!process.client || !this.email) {
        return false
      }
      try {
        const stateJson = localStorage.getItem(this.getOtpStorageKey())
        if (!stateJson) {
          return false
        }
        const state = JSON.parse(stateJson)

        // Check if state matches current email
        if (state.email !== this.email) {
          return false
        }

        // Calculate remaining time
        const now = Date.now()
        const remainingMs = state.expiresAt - now
        const remainingSec = Math.max(0, Math.floor(remainingMs / 1000))

        // Only restore if not expired
        if (remainingSec > 0) {
          this.otpMethod = state.otpMethod || 'email'
          this.showOtpInput = state.showOtpInput || false
          this.startOtpTimer(remainingSec)
          return true
        } else {
          // Expired, clear it
          this.clearOtpState()
          return false
        }
      } catch (error) {
        console.warn('Failed to restore OTP state from localStorage:', error)
        return false
      }
    },
    clearOtpState() {
      if (!process.client || !this.email) {
        return
      }
      try {
        localStorage.removeItem(this.getOtpStorageKey())
      } catch (error) {
        console.warn('Failed to clear OTP state from localStorage:', error)
      }
    },
    async resendOtp() {
      if (this.resendingOtp) {
        return
      }
      try {
        this.resendingOtp = true
        this.$nuxt.$loading?.start()
        this.error = ''
        this.otpError = ''

        // Resend using current method (or forced email)
        const requestData: any = { email: this.email.trim() }
        if (this.forceEmailOtp) {
          requestData.method = 'email'
        }

        const response = await this.$axios.post('/proxyapi/request-otp', requestData)

        // Check if user needs to register
        if (response.data?.requires_registration) {
          this.$nuxt.$loading?.finish()
          // Redirect to signup page with email prefilled
          this.$router.push(this.localePath({ name: 'signup', query: { email: this.email.trim() } }))
          return
        }

        // Update method from response
        this.otpMethod = response.data?.method_used || this.otpMethod

        // Restart timer with remaining_seconds from backend if available
        const remainingTime = response.data?.remaining_seconds || 300
        this.startOtpTimer(remainingTime)
        // Save state to localStorage for recovery after page refresh
        this.saveOtpState()
        this.otp = ''

        // Show success message temporarily
        const successMsg = this.otpMethod === 'sms'
          ? this.$t('login.otpSentViaSms').toString()
          : this.otpMethod === 'whatsapp'
            ? this.$t('login.otpSentViaWhatsapp').toString()
            : this.$t('login.otpSent').toString()
        this.error = successMsg
        setTimeout(() => {
          if (this.error === successMsg) {
            this.error = ''
          }
        }, 3000)

        this.$nuxt.$loading?.finish()
      } catch (error: any) {
        this.$nuxt.$loading?.finish()
        this.error = error.response?.data?.error || this.$t('login.otpRequestError').toString()
      } finally {
        this.resendingOtp = false
      }
    },
    changeEmail() {
      this.showOtpInput = false
      this.otp = ''
      this.otpError = ''
      this.error = ''
      this.otpMethod = 'email'
      this.forceEmailOtp = false
      this.clearOtpTimer()
      this.clearOtpState()
      this.$nextTick(() => {
        document.getElementById('emailinput')?.focus()
      })
    },
    async switchToEmail() {
      // Force resend OTP via email
      this.forceEmailOtp = true
      this.otp = ''
      this.otpError = ''

      try {
        this.resendingOtp = true
        this.$nuxt.$loading?.start()

        const response = await this.$axios.post('/proxyapi/request-otp', {
          email: this.email.trim(),
          method: 'email'
        })

        // Check if user needs to register
        if (response.data?.requires_registration) {
          this.$nuxt.$loading?.finish()
          // Redirect to signup page with email prefilled
          this.$router.push(this.localePath({ name: 'signup', query: { email: this.email.trim() } }))
          return
        }

        this.otpMethod = response.data?.method_used || 'email'
        // Use remaining_seconds from backend if available
        const remainingTime = response.data?.remaining_seconds || 300
        this.startOtpTimer(remainingTime)
        // Save state to localStorage for recovery after page refresh
        this.saveOtpState()

        // Show success message temporarily
        this.error = this.$t('login.otpSentViaEmail').toString()
        setTimeout(() => {
          if (this.error === this.$t('login.otpSentViaEmail').toString()) {
            this.error = ''
          }
        }, 3000)

        this.$nuxt.$loading?.finish()
      } catch (error: any) {
        this.$nuxt.$loading?.finish()
        this.error = error.response?.data?.error || this.$t('login.otpRequestError').toString()
      } finally {
        this.resendingOtp = false
      }
    },
    async handleWebAuthnSuccess(userData: any) {
      try {
        // Set auth token (critical - must be done before getUserData)
        this.$auth.setUserToken(userData.token)

        // Set locale based on user preference
        this.$i18n.setLocale(userData.locale || 'it')
        this.$dayjs.locale(userData.locale || 'it')

        // Track login event
        const encryptedEmail = (this as any).$CryptoJS.AES.encrypt(this.email.trim(), this.secret).toString()
        this.$gtm.push({
          event: 'login',
          event_data: {
            login_platform: 'main_site',
            login_method: 'webauthn',
            user_id: encryptedEmail,
            page_url: this.$router.currentRoute.fullPath,
            page_title: 'Login - CVing'
          }
        })

        // Track ORP if present
        if (this.orpSuggestion?.cid) {
          this.track('login', 'end', 'success')
        }

        // Get fresh user data
        await this.getUserData()
      } catch (error) {
        console.error('Error handling WebAuthn success:', error)
        this.error = this.$t('login.genericError').toString()
      }
    },

    /**
     * Get device name for passkey registration
     */
    getDeviceName(): string {
      if (typeof navigator === 'undefined') {
        return 'My Device'
      }
      const ua = navigator.userAgent
      if (/iPhone/.test(ua)) { return 'iPhone' }
      if (/iPad/.test(ua)) { return 'iPad' }
      if (/Macintosh/.test(ua)) { return 'Mac' }
      if (/Android/.test(ua)) { return 'Android' }
      if (/Windows/.test(ua)) { return 'Windows' }
      if (/Linux/.test(ua)) { return 'Linux' }
      return 'My Device'
    },

    /**
     * Try to register a passkey after login/signup
     * Shows native browser dialog directly without UI prompt
     */
    async tryRegisterPasskey() {
      // Skip if WebAuthn not available or in-app browser
      if (!this.$webauthnUtils?.isAvailable() || this.$webauthnUtils?.isInAppBrowser()) {
        return
      }

      // Skip if user already dismissed the prompt (check both possible field names)
      if (this.$auth?.user?.passkey_prompt_dismissed || this.$auth?.user?.passkey_prompt_dismissed_at) {
        return
      }

      // Skip if user already has a passkey
      try {
        const hasPasskey = await this.$webauthn.hasPasskeyRegistered()
        if (hasPasskey) {
          return
        }
      } catch {
        return
      }

      // Auto-detect device name
      const deviceName = this.getDeviceName()

      // Call register() - shows native browser/OS dialog
      const result = await this.$webauthn.register(deviceName)

      if (!result.success) {
        // If user cancelled, dismiss prompt so we don't ask again
        if (result.error?.name === 'NotAllowedError') {
          await this.$webauthn.dismissPasskeyPrompt()
        }
      }
    }
  }
})
